// #23 ValueChips: the five ROOTS value chips RES · OWN · OPP · TOG · SER, each a 28 px checkbox (role="checkbox",
// aria-checked) named by the value's full word (*Respect* …, which holds the abbreviation: WCAG 2.5.3) and mirrored by
// a tooltip. A group labelled by `label` or `aria-labelledby`; each chip is its own tab stop (a checkbox set, not a
// radiogroup). `fixed` (the HM interview's all five): every chip ticked on the green tint, aria-disabled. Blocked
// (DC-24, "D values"): the chosen chips stay marked, every chip is aria-disabled and described by the inline reason.
// mode="view": the chosen abbreviations as text, each named in full for assistive technology.

import { useHs, type Hs } from "../lib/hs";
import { cx } from "../lib/cx";
import { Tooltip } from "../lib/Tooltip";
import { VisuallyHidden } from "../lib/VisuallyHidden";
import { ROOTS_ABBR_LABELS, ROOTS_LABELS, ROOTS_ORDER, type RootsValue } from "../labels";
import { useBlocked, type ControlProps } from "../core/Button";

export interface ValueChipsProps extends Omit<ControlProps, "hs"> {
  /** The values ticked (ignored when `fixed`). */
  value: readonly RootsValue[];
  onChange?: ((value: RootsValue[]) => void) | undefined;
  /** All five, always: the HM interview assesses every value. */
  fixed?: boolean | undefined;
  /** The group's name (aria-label), when no visible label names it. */
  label?: string | undefined;
  "aria-labelledby"?: string | undefined;
  /** Hotspots per value. */
  hs?: Partial<Record<RootsValue, Hs>> | undefined;
  /** The lane's full-width five-column form. */
  fill?: boolean | undefined;
  className?: string | undefined;
}

function Chip({ value, on, fixed, inert, describedBy, hs, onToggle }: {
  value: RootsValue;
  on: boolean;
  fixed: boolean;
  inert: boolean;
  describedBy: string | undefined;
  hs: Hs | undefined;
  onToggle: () => void;
}) {
  const hot = useHs(hs);
  return (
    <Tooltip label={ROOTS_LABELS[value]} mode="mirror">
      <button
        type="button"
        role="checkbox"
        aria-checked={on}
        aria-label={ROOTS_LABELS[value]}
        aria-disabled={inert ? true : undefined}
        aria-describedby={describedBy}
        className={cx("mc-vchip", on && !fixed && "mc-on", fixed && "mc-vchip-fixed", inert && !fixed && "mc-dis")}
        {...hot}
        onClick={() => { if (!inert) onToggle(); }}
      >
        <span aria-hidden="true">{ROOTS_ABBR_LABELS[value]}</span>
      </button>
    </Tooltip>
  );
}

export function ValueChips({ value, onChange, fixed = false, label, "aria-labelledby": labelledBy, hs, fill, can, blocked, mode, className }: ValueChipsProps) {
  const block = useBlocked(blocked);
  if (can === false) return null;
  const ticked = (key: RootsValue) => fixed || value.includes(key);
  if (mode === "view") {
    return (
      <span className={cx("mc-vchips", "mc-vchips-view", fill && "mc-vchips-fill", className)}>
        {ROOTS_ORDER.filter(ticked).map((key) => (
          <span key={key} className={cx("mc-vchip", fixed ? "mc-vchip-fixed" : "mc-on")}>
            <span aria-hidden="true">{ROOTS_ABBR_LABELS[key]}</span>
            <VisuallyHidden>{ROOTS_LABELS[key]}</VisuallyHidden>
          </span>
        ))}
      </span>
    );
  }
  const inert = fixed || Boolean(blocked);
  const toggle = (key: RootsValue) => {
    const next = value.includes(key) ? value.filter((v) => v !== key) : ROOTS_ORDER.filter((v) => v === key || value.includes(v));
    onChange?.(next);
  };
  return (
    <span
      role="group"
      aria-label={labelledBy ? undefined : label}
      aria-labelledby={labelledBy}
      className={cx("mc-vchips", fill && "mc-vchips-fill", className)}
    >
      {ROOTS_ORDER.map((key) => (
        <Chip
          key={key}
          value={key}
          on={ticked(key)}
          fixed={fixed}
          inert={inert}
          describedBy={block.describedBy}
          hs={hs?.[key]}
          onToggle={() => toggle(key)}
        />
      ))}
      {block.reason}
    </span>
  );
}
